/**
 * Client-side mirror of the backend password policy in
 * services/identity/internal/domain/password.go.
 *
 * This exists purely so the operator sees the requirement before submitting;
 * the server is the authority and rejects weak passwords regardless. If the
 * two ever disagree, the server wins — keep this in step when the Go policy
 * changes.
 */

export const MIN_PASSWORD_LENGTH = 10

const COMMON_PASSWORDS = new Set([
  'password',
  'password1',
  'password123',
  'passw0rd',
  'admin',
  'adminadmin',
  'administrator',
  'welcome1',
  'letmein',
  'qwerty',
  'qwerty123',
  'iloveyou',
  'changeme',
  'secret',
  'omnitrace',
  'omnitrace1',
  '12345678',
  '123456789',
  '1234567890',
])

/**
 * Returns null when the password satisfies the policy, otherwise a short
 * message explaining what is missing (safe to render next to the field).
 */
export function passwordError(pw: string): string | null {
  if ([...pw].length < MIN_PASSWORD_LENGTH) {
    return `Must be at least ${MIN_PASSWORD_LENGTH} characters`
  }
  if (COMMON_PASSWORDS.has(pw.toLowerCase())) {
    return 'Too common — choose something less predictable'
  }

  const classes = [
    /\p{Ll}/u.test(pw), // lowercase
    /\p{Lu}/u.test(pw), // uppercase
    /\p{Nd}/u.test(pw), // digit
    /[^\p{Ll}\p{Lu}\p{Nd}]/u.test(pw), // symbol or space
  ].filter(Boolean).length

  if (classes < 3) {
    return 'Must combine at least three of: lowercase, uppercase, digits, symbols'
  }
  return null
}

/** Convenience predicate for disabling submit buttons. */
export function isPasswordValid(pw: string): boolean {
  return passwordError(pw) === null
}
